import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import CastIcon from "./CastIcon";
import MiniPlayerInfo, { type DockMini } from "./MiniPlayerInfo";
import { ownCastFullScreenButtons, ownCastLabel, useOwnCastActions } from "./OwnCastControls";
import { watchedCastStopButton } from "./WatchedCastControls";
import { castControlsSpec, type CastFullScreen } from "./castControlsSpec";
import VmplFilePlayer from "./VmplFilePlayer";
import CastQueueLine from "./CastQueueLine";
import VmplLinkPlayer from "./VmplLinkPlayer";
import { linkItemOf } from "../../lib/castQueue";
import { iconFor, kindIcon, resolveItem } from "../../player";
import { linkUrlOf } from "../../lib/linkPlayer";
import { useCastQueueStore } from "../../store/castQueueStore";
import type { DockCast } from "../../lib/castDock";
import type { PlayerState } from "../../lib/castSync/castFollower";
import { serverClock } from "../../lib/castSync/serverClock";
import { positionAt, timelineOf } from "../../lib/castSync/timeline";
import type { StreamSlot } from "../../types/stream";

export interface CastDockCardProps {
  cast: DockCast;
  /** Live slot data (status store, or the own-cast record). */
  slot: StreamSlot;
  /**
   * Own: play/pause/stop; watched: hide (Task 14). A function gets the player's position now
   * (its last report, projected while playing; else the slot's timeline).
   */
  controls?: React.ReactNode | ((positionMs: () => number) => React.ReactNode);
  /** Your own cast only: its player is stuck (Android `stallHandler`). */
  onStall?: (positionMs: number) => void;
  /** Your own link cast only (CastAdvancer): the player finished this url. */
  onItemEnded?: (url: string) => void;
  /** Your own link cast only (CastAdvancer): this url cannot play. */
  onItemFailed?: (url: string) => void;
  onExpand(): void;
  /** Only on the PiP target when no live card shows. */
  onPopOut?: () => void;
  /** Turns the dock into the bottom mini player, this card first (owner 2026-10-21). */
  onMinimise?: () => void;
  /** The dock is the mini player and this card is the one it shows. */
  mini?: DockMini | null;
  /**
   * The dock is the mini player and another player shows: not displayed. A watched YouTube / SoundCloud player is
   * unmounted meanwhile (never plays hidden) and resyncs to the timeline when it shows again.
   */
  hidden?: boolean;
}

const btn = "w-7 h-7 rounded-md bg-black/45 text-white text-[13px] flex items-center justify-center";

/** One cast in the dock: its marker (never LIVE), the synced player, and the controls its kind has. */
export default function CastDockCard({ cast, slot, controls, onStall, onItemEnded, onItemFailed, onExpand, onPopOut, onMinimise, mini = null, hidden = false }: CastDockCardProps) {
  const { t } = useTranslation();
  const lastReport = useRef<{ positionMs: number; state: PlayerState; atMono: number } | null>(null);
  const slotRef = useRef(slot);
  slotRef.current = slot;
  const onPosition = useCallback((positionMs: number, state: PlayerState) => {
    lastReport.current = { positionMs, state, atMono: serverClock.monoNow() };
  }, []);
  // Android `runner.lastPositionMs ?: timeline.positionAt(now) ?: 0`.
  const positionMs = useCallback((): number => {
    const r = lastReport.current;
    if (r) return r.positionMs + (r.state === "playing" ? Math.max(0, serverClock.monoNow() - r.atMono) : 0);
    const tl = timelineOf(slotRef.current);
    return tl ? positionAt(tl, serverClock.now()) : 0;
  }, []);
  const isFile = cast.localId !== "VMPL_LINK_1";
  const queue = useCastQueueStore((s) => s.bySlot[slot.id]?.queue ?? null);
  // The link cast's current item and its kind (the player registry decides the player and what the card shows).
  const item = isFile ? null : linkItemOf(slot, queue);
  const resolved = item ? resolveItem(item) : null;
  const kind = resolved?.kind ?? null;
  const url = isFile ? null : linkUrlOf(slot);
  const itemTitle = (url != null && queue?.items.find((i) => i.url === url)?.title) || null;
  // This browser cannot decode it (an Ogg radio on Safari): "Can't play here". Nothing is reported, so the queue
  // goes on as the caster moves it.
  const unplayable = resolved != null && !resolved.playable;
  const name = slot.streamingUser?.name ?? "";
  const label = cast.own
    ? ownCastLabel(t, cast.localId)
    : isFile ? t("dock_label_file", "{{name}}'s file", { name }) : t("dock_label_link", "{{name}}'s link", { name });
  // An audio item has no picture: the mini player shows its art instead of the player box.
  const audio = kind?.surface === "audio" || unplayable;
  const miniVideo = mini != null && !audio;
  const controlsNode = typeof controls === "function" ? controls(positionMs) : controls;
  // Full screen (the player's frame): the shared controls replace the card's bar and queue line (one set of tags).
  const [fsOn, setFsOn] = useState(false);
  const ownActions = useOwnCastActions(cast.localId, positionMs);
  const ownButtons = ownCastFullScreenButtons(t, ownActions);
  const position = !isFile && queue && queue.count > 1 ? t("player_item_of", "{{n}} of {{total}}", { n: queue.index + 1, total: queue.count }) : null;
  const fullScreen: CastFullScreen = {
    spec: ({ exit, seek, embed }) =>
      castControlsSpec({
        own: cast.own,
        title: itemTitle ?? label,
        meta: [itemTitle ? label : null, position].filter(Boolean).join(" · ") || undefined,
        exit,
        stop: cast.own ? ownButtons.stop : watchedCastStopButton(t, cast.key),
        prev: ownButtons.prev,
        playPause: ownButtons.playPause,
        next: ownButtons.next,
        belowTop: isFile ? undefined : <CastQueueLine slotId={slot.id} fullScreen />,
        seek,
        embed,
      }),
    dialog: cast.own ? ownActions.confirmDialog : null,
    onChange: setFsOn,
  };
  const player = !item || !resolved ? (
    <VmplFilePlayer slot={slot} onStall={onStall} onPosition={onPosition} fullScreen={fullScreen} />
  ) : (
    // Every item of a link cast: the player module picks the player from the kind (and "Can't play here").
    <VmplLinkPlayer slot={slot} item={item} resolved={resolved} title={itemTitle} fill={miniVideo} onStall={onStall} onPosition={onPosition} onEnded={onItemEnded} onLoadError={onItemFailed} fullScreen={fullScreen} />
  );
  // Hidden behind the mini player's top player: a watched player that must not play unseen (YouTube terms) unmounts.
  // Your own one is never hidden (it takes the mini player first, lib/dockMini).
  const unmountPlayer = hidden && kind != null && kind.caps.heldWhenHidden && !cast.own;
  const ArtGlyph = kind ? kindIcon(kind) : iconFor("music");
  return (
    // shrink-0 (root and every block): in the capped dock column an overflow-hidden card would
    // otherwise absorb the overflow down to 0 px and hide a playing YouTube iframe; the dock scrolls.
    // The player box stays the second child in every form: floating ↔ mini never remounts it (playback goes on).
    <div
      data-testid={`cast-card-${cast.key}`}
      data-cast-card={cast.key}
      data-mini={mini ? (miniVideo ? "video" : "audio") : undefined}
      className={
        mini
          ? `flex shrink-0 flex-row gap-3 overflow-hidden rounded-xl bg-[var(--bg-base)] p-2 shadow-lg ${miniVideo ? "items-stretch" : "items-center"}`
          : "flex shrink-0 flex-col overflow-hidden [&>*]:shrink-0 rounded-lg border border-[var(--border)] bg-black shadow-lg"
      }
      style={hidden ? { display: "none" } : undefined}
    >
      {mini ? (
        !miniVideo && (
          <div className="size-12 shrink-0 rounded-lg bg-[var(--bg-elevated)] text-[var(--foreground-muted)] flex items-center justify-center">
            <ArtGlyph size={24} aria-hidden="true" />
          </div>
        )
      ) : (
        <div className="flex items-center gap-2 px-2 py-1 text-[11px] text-slate-200">
          <CastIcon size={14} />
          <span className="truncate">{label}</span>
        </div>
      )}
      <div
        data-testid={`cast-player-box-${cast.key}`}
        className={mini ? (miniVideo ? "size-[200px] shrink-0 overflow-hidden rounded-xl bg-black flex flex-col justify-center" : "hidden") : undefined}
      >
        {unmountPlayer ? null : player}
      </div>
      {!mini && !isFile && !fsOn && <CastQueueLine slotId={slot.id} />}
      {mini ? (
        <MiniPlayerInfo
          mini={mini}
          title={itemTitle ?? label}
          line={itemTitle ? label : name}
          video={miniVideo}
          playing={slot.playStatus === "PLAY"}
          onTogglePlay={null}
          onStop={null}
          controls={controlsNode}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-1 px-2 py-1">
          {!fsOn && controlsNode}
          {onMinimise && (
            <button type="button" data-testid={`dock-minimise-${cast.key}`} onClick={onMinimise} className={btn} title={t("dock_collapse", "Collapse")} aria-label={t("dock_collapse", "Collapse")}>
              <ChevronDown size={16} aria-hidden="true" />
            </button>
          )}
          {onPopOut && (
            <button type="button" data-testid="dock-popout" onClick={onPopOut} className={btn} title={t("pip_pop_out")} aria-label={t("pip_pop_out")}>⧉</button>
          )}
          <button type="button" data-testid="dock-expand" onClick={onExpand} className={btn} title={t("pip_expand")} aria-label={t("pip_expand")}>⤢</button>
        </div>
      )}
    </div>
  );
}
